import styled from '@emotion/styled';
import IconButton from '@base/IconButton/IconButton';
import H4 from '@base/H4/H4';
import P1 from '@base/P1/P1';
import Button from '@base/Button/Button';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';

export const StyH4 = styled(H4)`
  color: ${({ $isPrime }) =>
    $isPrime ? 'var(--color-i-white)' : 'var(--color-text-primary)'};

  ${({ $isPrime }) =>
    $isPrime
      ? `
    @media (max-width: 768px) {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 100%;
    }
  `
      : ''}
`;

export const BoldP1 = styled(P1)`
  font-weight: ${Fonts.BOLD};
`;

export const ItemWrap = styled.div`
  grid-column: ${({ gridColumn }) => gridColumn};
  opacity: ${({ opacity }) => opacity};
  display: flex;
  transition: opacity 1000ms ease-in, transform 400ms ease-in !important;
  ${mediaQuery.uptoMobile} {
    opacity: 1;
    grid-row: 1;
    grid-column: span 1;
  }
`;

export const CarouselWrapper = styled.div`
  grid-column: ${({ gridColumn }) => gridColumn};
  position: relative;
  width: calc(100% + ${Spacings.SPACING_8B});
  margin-left: -${Spacings.SPACING_4B};
  margin-right: -${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    margin-left: 0;
    margin-right: 0px;
    width: 100%;
    ${({ $css }) => $css}
    ${({ $hasSeeAll }) => ($hasSeeAll ? 'margin-bottom:0;' : '')}
  }
`;

export const CardContainer = styled.div`
  display: grid;
  grid-template-columns: ${({ $gridTemplateColumns }) =>
    `repeat(${$gridTemplateColumns}, 32px)`};
  grid-column-gap: ${({ $gridColumnGap }) =>
    $gridColumnGap || Spacings.SPACING_4B};
  overflow: hidden;
  padding-left: ${Spacings.SPACING_4B};
  padding-bottom: ${({ $showBottomGap }) =>
    $showBottomGap ? Spacings.SPACING_9B : '0px'};
  & > a,
  & > div {
    transform: ${({ pos }) =>
      (pos && `translate3d(calc(${-100 * pos}% - ${pos * 16}px), 0, 0)`) ||
      'initial'};
    transition: transform 400ms ease-in;
  }
  ${mediaQuery.uptoMobile} {
    padding: 0 ${Spacings.SPACING_4B} ${Spacings.SPACING_6B}
      ${Spacings.SPACING_4B};
    margin: 0 -${Spacings.SPACING_4B};
    overflow: scroll;
    overscroll-behavior: unset;
    scroll-behavior: smooth;
    grid-template-columns: ${({
      $numberOfItemsToShow,
      $nItems,
      $mobileItemMinWidth
    }) => `repeat(
  ${$nItems},
  minmax(
    ${$numberOfItemsToShow === 1 ? '100%' : $mobileItemMinWidth || '250px'},
    1fr
  )
)`};
    ::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

export const ControlButton = styled(IconButton)`
  position: absolute;
  right: ${({ $isUnIconArrow }) => $isUnIconArrow && '-16px'} !important;
  ${mediaQuery.uptoIconTablet} {
    right: ${({ $isUnIconArrow }) => $isUnIconArrow && '0px'};
  }

  transition: visibility 0s linear 300ms, opacity 300ms;
  top: calc(50% - 16px - ${({ $topAdjustment }) => $topAdjustment});

  box-shadow: ${({ $isPrime }) => ($isPrime ? 'none' : boxShadowGenerator())};

  border: ${({ $isPrime }) => ($isPrime ? '1px solid #FFFFFF40' : 'none')};

  background: ${({ $isPrime, bgColor }) =>
    $isPrime ? 'var(--color-i-prime-card)' : bgColor};

  ${({ direction }) => (direction === 'prev' ? 'left' : 'right')}: 0px;

  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

export const CaraouselInfo = styled.div`
  display: flex;
  margin-bottom: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_7B};
  align-items: center;
  padding: 0;
  grid-column: span 18;
  @media (max-width: 70.95em) {
    margin: 0 0 ${Spacings.SPACING_6B};
  }
`;

export const NavButtonsContainer = styled.div`
  grid-column: ${({ $gridColumn }) => $gridColumn};
  padding: ${Spacings.SPACING_2B};
  padding-bottom: ${Spacings.SPACING_2B};
  display: flex;
  justify-content: center;
  margin-top: 0px;
`;

export const NavButton = styled.div`
  width: ${Spacings.SPACING_2B};
  height: ${Spacings.SPACING_2B};
  border-radius: 50%;
  background-color: ${({ $isSelected }) =>
    $isSelected ? 'var(--color-text-secondary)' : 'var(--color-divider)'};
  margin: 0 ${Spacings.SPACING_1B};
  cursor: pointer;
`;

export const ScrollDotsContainer = styled.div`
  grid-column: ${({ $gridColumn }) => $gridColumn};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${Spacings.SPACING_2B} 0;
`;

export const ScrollDot = styled.div`
  width: ${Spacings.SPACING_2B};
  height: ${Spacings.SPACING_2B};
  border-radius: 50%;
  background: var(--color-i-white);
  opacity: ${({ $isActive }) => ($isActive ? 1 : 0.5)};
  margin: 0 ${Spacings.SPACING_1B};
`;

export const SeeAll = styled(Button)`
  display: none;
  ${mediaQuery.uptoMobile} {
    display: block;
    min-height: ${Spacings.SPACING_10B};
    height: ${Spacings.SPACING_10B};
    width: 100%;
    background: var(--color-i-white);
    border: 1px solid var(--color-divider);
    border-radius: ${Spacings.SPACING_2B};
    color: var(--color-i-green);
    font-size: 14px;
    margin: ${Spacings.SPACING_4B} 0 ${Spacings.SPACING_14B};
  }
`;

export const InfoSectionsDiv = styled.div`
  min-width: 0;
  flex: 1;
`;

export const Description = styled(Typography)`
  color: var(--color-text-secondary);
  margin-top: ${Spacings.SPACING_1B};
`;
